import React, { memo, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { useTheme, createUseStyles, Section, Text, Modal, Icon } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from 'lib/Web5Context';
import CarouselFullModal from '../../components/CarouselFullModal';
import Image from './Image';
import ModalHeaderLeft from './ModalHeaderLeft';
import ModalHeaderCenter from './ModalHeaderCenter';
import ModalHeaderRight from './ModalHeaderRight';

import styles from './styles';
const useStyles = createUseStyles(styles, 'UploadImageCarousel');

const UploadImageCarousel = ({ limit, onDelete, onAdd, carouselFullModalClass, images = [] }) => {
    const classes = useStyles();
    const theme = useTheme();
    const { t } = useWeb5Context();
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [currentImage, setCurrentImage] = useState(null);
    const [deleteImage, setDeleteImage] = useState(null);

    const handleDelete = useCallback((imageId) => {
        setDeleteImage(imageId);
    }, []);

    const handleConfirmDelete = useCallback(() => {
        onDelete(deleteImage);
        setDeleteImage(null);
    }, [deleteImage, onDelete]);

    const handleCancelDelete = useCallback(() => {
        setDeleteImage(null);
    }, []);

    const renderContentLeft = useCallback(
        () => (
            <ModalHeaderLeft name={(images[currentImage] && images[currentImage].name) || null} />
        ),
        [currentImage, images],
    );
    const renderContentCenter = useCallback(
        () => (
            <ModalHeaderCenter index={currentImage} totalImages={(images && images.length) || 0} />
        ),
        [currentImage, images],
    );
    const renderContentRight = useCallback(
        () => <ModalHeaderRight index={currentImage} onDelete={handleDelete} />,
        [currentImage, handleDelete],
    );

    const onDrop = useCallback(
        (acceptedFiles) => {
            if (!acceptedFiles) return;
            const imagesLength = Object.keys(images).length;
            if (acceptedFiles.length > limit - imagesLength) {
                //Toast
                console.warn(`You can't upload more than ${limit - imagesLength} images.`);
                return;
            }
            const validFiles = acceptedFiles.filter((file) => {
                if (
                    file.type !== 'image/jpeg' &&
                    file.type !== 'image/png' &&
                    file.type !== 'image/gif'
                ) {
                    //Toast
                    console.warn('File type not supported');
                    return false;
                } else if (file.size > 16777216) {
                    //Toast
                    console.warn('File size error');
                    return false;
                } else {
                    return true;
                }
            });
            onAdd(validFiles);
        },
        [images, limit, onAdd],
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

    const updateImageSelected = useCallback(
        (index) => {
            setCurrentImage(index);
        },
        [setCurrentImage],
    );

    const openViewImage = useCallback((index) => {
        setCurrentImage(index);
        setModalIsOpen(true);
    }, []);

    const closeViewImage = useCallback(() => {
        setModalIsOpen(false);
    }, []);

    const imagesList = useMemo(() => {
        if (!images) return null;
        return images.map((el, i) => (
            <Image key={el.id} img={el} index={i} onDelete={handleDelete} onView={openViewImage} />
        ));
    }, [openViewImage, images, handleDelete]);

    const showDropZone = useMemo(() => images && images.length < limit, [images, limit]);

    const modalOverrides = useMemo(() => ({ confirmButton: { color: 'danger' } }), []);

    return (
        <div className={classes.root}>
            <Section title={t('label_photo_gallery')} className={classes.section}>
                <div className={classes.container}>
                    <div className={classes.carousel}>{imagesList}</div>
                    {showDropZone && (
                        <div className={classes.dropZone} {...getRootProps()}>
                            <input {...getInputProps()} />
                            {isDragActive ? (
                                <div className={classes.dropZoneActive}>
                                    <Text className={classes.dropText}>
                                        {t('cfm_label_drop_to_upload')}
                                    </Text>
                                </div>
                            ) : (
                                <div className={classes.dropZoneInactive}>
                                    <Icon
                                        name="cloudUpload"
                                        className={classes.cloudUploadIcon}
                                        color={theme.colors.utility.textSecondary}
                                    />
                                    <Text className={classes.dropText}>
                                        {t('cfm_label_drop_to_upload')}
                                    </Text>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </Section>
            <CarouselFullModal
                isOpen={modalIsOpen || false}
                withCloseIcon={true}
                onRequestClose={closeViewImage}
                updateImageSelected={updateImageSelected}
                contentLeft={renderContentLeft}
                contentCenter={renderContentCenter}
                contentRight={renderContentRight}
                images={images}
                imageSelected={currentImage !== null ? currentImage : 0}
                className={carouselFullModalClass}
            />
            <Modal
                title={t('title_warning')}
                isOpen={deleteImage !== null}
                size="small"
                onRequestClose={handleCancelDelete}
                onCancel={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                cancelText={t('cfm_action_yes')}
                confirmText={t('cfm_action_no')}
                overlayClassName={classes.deleteModalOverlay}
                overrides={modalOverrides}
            >
                <Text>{t('label_confirm_delete_image')}</Text>
            </Modal>
        </div>
    );
};

UploadImageCarousel.propTypes = {
    limit: PropTypes.number.isRequired,
    images: PropTypes.array.isRequired,
};

export default memo(UploadImageCarousel);
